import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SEO from '../components/SEO';
import buildingImg from '../assets/building.png';

const BookVisit = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    project: '',
    requirements: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { error } = await supabase.from('requests').insert([{
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        type: 'Site Visit',
        message: `Project: ${formData.project}\nDate: ${formData.date}\nTime: ${formData.time}\n\nRequirements: ${formData.requirements}`
      }]);
      
      if (error) throw error;
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', date: '', time: '', project: '', requirements: '' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Error submitting visit request:', err);
      alert('Failed to book visit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans text-white">
      <SEO 
        title="Book a Site Visit | HBB0011 REALTY"
        description="Schedule a personalized site visit to experience our luxury real estate projects firsthand. Book your appointment with HBB0011 REALTY today."
        keywords="book site visit, real estate tour, property viewing, luxury property visit"
      />
      
      {/* 1. Hero Section (No Founder Image) */}
      <section className="relative w-full pt-32 pb-24 md:pt-40 md:pb-32 flex items-center overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 z-0">
          <img src={buildingImg} alt="Luxury Real Estate" className="w-full h-full object-cover opacity-20 mix-blend-luminosity" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/90 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center gap-2 text-xs sm:text-sm font-bold tracking-widest mb-6 uppercase">
            <Link to="/" className="text-white hover:text-[#D4AF37]">Home</Link>
            <span className="text-gray-500 material-symbols-outlined text-sm">chevron_right</span>
            <span className="text-[#D4AF37]">Book a Site Visit</span>
          </div>

          <div className="max-w-3xl text-left">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight uppercase">
              Book a <br/><span className="text-[#D4AF37]">Site Visit</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-300 mb-4 font-light italic">
              Experience our iconic projects firsthand.
            </p>
            <p className="text-sm sm:text-base text-gray-400 max-w-xl leading-relaxed">
              Step into the world of HBB0011 Realty Realtors. Visit our projects, explore the spaces, and envision your future with us.
            </p>
          </div>
        </div>
      </section>

      {/* 2. Benefits Strip */}
      <section className="py-12 bg-[#06121e] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-5 gap-8">
          {[
            { icon: 'map', title: 'Personalized Site Tour' },
            { icon: 'support_agent', title: 'Expert Consultation' },
            { icon: 'architecture', title: 'Explore Amenities' },
            { icon: 'location_on', title: 'Prime Locations' },
            { icon: 'event', title: 'Flexible Scheduling' }
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center text-center">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light mb-3">{item.icon}</span>
              <p className="text-[10px] font-bold text-white uppercase tracking-widest leading-relaxed">{item.title}</p>
            </div>
          ))}
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Booking Form */}
          <div className="lg:col-span-7 bg-[#06121e] border border-gray-800 p-8 sm:p-12 rounded-sm shadow-xl">
            <h2 className="text-2xl text-white font-bold mb-2 uppercase tracking-wide">Book Your Site Visit</h2>
            <p className="text-gray-400 text-sm mb-10">Fill in your details and our team will get in touch with you to confirm your visit.</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {success && (
                <div className="bg-green-500/20 border border-green-500/50 text-green-400 p-4 rounded-sm text-sm">
                  Your site visit request has been sent! Our team will contact you to confirm.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Full Name" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="Email Address" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="Phone Number" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                <input type="date" name="date" value={formData.date} onChange={handleInputChange} required className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-gray-400 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                <input type="time" name="time" value={formData.time} onChange={handleInputChange} required className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-gray-400 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors" />
                <select name="project" value={formData.project} onChange={handleInputChange} required className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-gray-400 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors">
                  <option value="">Select Project</option>
                  <option value="Signature Tower">Signature Tower</option>
                  <option value="HBB Canal View">HBB Canal View</option>
                </select>
              </div>
              <textarea rows="4" name="requirements" value={formData.requirements} onChange={handleInputChange} placeholder="Special Requirements" className="w-full bg-[#041424] border border-gray-700 rounded-sm px-4 py-4 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors resize-none"></textarea>
              
              <div className="flex items-start gap-3">
                <input type="checkbox" id="terms" required className="mt-1 accent-[#D4AF37]" />
                <label htmlFor="terms" className="text-gray-400 text-xs">I agree to the <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms & Conditions</Link>.</label>
              </div>

              <button type="submit" disabled={loading} className={`w-full font-bold text-xs uppercase tracking-widest py-4 rounded-sm transition-colors flex items-center justify-center gap-2 ${loading ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-[#D4AF37] text-[#041424] hover:bg-white'}`}>
                {loading ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-base">sync</span> Processing...
                  </>
                ) : (
                  <>
                    Book Site Visit <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-[#06121e] border border-gray-800 p-8 rounded-sm">
              <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-6">Why Visit Our Projects?</h4>
              <ul className="space-y-4">
                {[
                  'Get a real feel of our construction quality',
                  'Explore thoughtfully designed spaces',
                  'Understand the neighborhood benefits',
                  'Interact with our experts',
                  'Make informed decisions'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-400 text-xs sm:text-sm">
                    <span className="material-symbols-outlined text-[#D4AF37] text-base">check_circle</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Request Callback */}
            <div className="border border-gray-800 rounded-sm p-8 flex items-center gap-6 bg-[#06121e]">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl">headset_mic</span>
              <div>
                <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-1">Need Help?</h4>
                <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-2">Schedule a call back</p>
                <button className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:text-white">
                  Request Callback <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
};

export default BookVisit;